import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis
} from 'ng-apexcharts';
import { AuthService } from '../service/auth/auth.service';
import { AdminActivityDay, AdminRestService } from '../service/rest/admin-rest.service';

export type AdminChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  legend: ApexLegend;
  fill: ApexFill;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  grid: ApexGrid;
  markers: ApexMarkers;
};

@Component({
  selector: 'app-admin-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, NgApexchartsModule],
  templateUrl: './admin-dashboard.component.html',
  styleUrl: './admin-layout.css'
})
export class AdminDashboardComponent implements OnInit {
  loading = true;
  stats: { totalUsers: number; activeSessions: number; contactMessagesUnread: number; testsLast30Days: number; openFraudEvents?: number } | null = null;
  activityTimeSeries: AdminActivityDay[] = [];
  chartOptions: Partial<AdminChartOptions> = {};

  constructor(
    private authService: AuthService,
    private adminRest: AdminRestService
  ) {}

  async ngOnInit() {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) return;
    try {
      const [stats, activity] = await Promise.all([
        this.adminRest.getAdminStats(sessionId),
        this.adminRest.getAdminActivity(sessionId)
      ]);
      this.stats = stats ?? null;
      if (activity?.activityTimeSeries) {
        this.activityTimeSeries = activity.activityTimeSeries;
        this.initializeChart(this.activityTimeSeries);
      }
    } finally {
      this.loading = false;
    }
  }

  private initializeChart(data: AdminActivityDay[]) {
    const usersData = data.map(d => ({
      x: new Date(d.date).getTime(),
      y: Number(d.totalNonAdminUsers) || 0
    }));
    const sessionsData = data.map(d => ({
      x: new Date(d.date).getTime(),
      y: Number(d.activeSessionsCount) || 0
    }));

    this.chartOptions = {
      series: [
        { name: 'Users (total non-admin)', data: usersData },
        { name: 'Active sessions', data: sessionsData }
      ],
      chart: {
        type: 'line',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      xaxis: {
        type: 'datetime',
        labels: { format: 'MMM dd' }
      },
      yaxis: {
        min: 0,
        tickAmount: 5,
        title: { text: 'Count' }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right'
      },
      grid: {
        borderColor: '#e0e0e0',
        strokeDashArray: 4
      },
      markers: { size: 5 }
    };
  }
}
